import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { reports } from '@/api/documents';
import { orgs, contractors as contractorsApi } from '@/api/catalogs';
import { refs } from '@/api/refs';

const fmt = (v: string | number) =>
  Number(v).toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const DOC_LABELS: Record<string, string> = {
  'doc.goods_receipts': 'Надходження',
  'doc.goods_sales': 'Реалізація',
  'doc.bank_receipts': 'Платіж (надход.)',
  'doc.bank_payments': 'Платіж (виплата)',
  'doc.cash_receipts': 'ПКО',
  'doc.cash_payments': 'РКО',
  'doc.goods_returns_from_customer': 'Повернення від покупця',
  'doc.goods_returns_to_supplier': 'Повернення постачальнику',
  'doc.debt_adjustments': 'Коригування боргу',
  'doc.additional_expenses': 'Додаткові витрати',
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

export default function ReconciliationActPrintPage() {
  const [params] = useSearchParams();
  const orgId = params.get('organization_id') ?? '';
  const contractorId = params.get('contractor_id') ?? '';
  const contractId = params.get('contract_id') ?? '';
  const dateFrom = params.get('date_from') ?? '';
  const dateTo = params.get('date_to') ?? '';

  const act = useQuery({
    queryKey: ['reconciliation-print', orgId, contractorId, contractId, dateFrom, dateTo],
    queryFn: () => reports.reconciliationAct({
      organization_id: orgId,
      contractor_id: contractorId,
      contract_id: contractId || undefined,
      date_from: `${dateFrom}T00:00:00+02:00`,
      date_to: `${dateTo}T23:59:59+02:00`,
    }),
    enabled: !!orgId && !!contractorId,
  });

  const orgsQ = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const contractorsQ = useQuery({
    queryKey: ['contractors-all'],
    queryFn: () => contractorsApi.list({ limit: 1000 }),
  });
  const contractsQ = useQuery({
    queryKey: ['contracts', orgId, contractorId],
    queryFn: () => refs.contracts({ organization_id: orgId, contractor_id: contractorId }),
    enabled: !!orgId && !!contractorId,
  });

  useEffect(() => {
    document.body.classList.add('print-mode');
    return () => document.body.classList.remove('print-mode');
  }, []);

  if (!act.data) {
    return <div style={{ padding: '2rem' }}>Завантаження…</div>;
  }

  const org = orgsQ.data?.items.find((o) => o.id === orgId);
  const ctr = contractorsQ.data?.items.find((c) => c.id === contractorId);
  const contract = contractsQ.data?.find((c) => c.id === contractId);

  const balance = Number(act.data.closing_balance);
  const balanceText = balance > 0
    ? `На користь ${org?.name ?? ''} — ${fmt(balance)} грн (контрагент винен)`
    : balance < 0
    ? `На користь ${ctr?.name ?? ''} — ${fmt(-balance)} грн (ми винні)`
    : 'Заборгованість відсутня';

  return (
    <div className="print-page" style={{
      padding: '20mm', fontFamily: 'Times, serif', fontSize: '11pt', lineHeight: 1.4,
      maxWidth: '210mm', margin: '0 auto', background: 'white',
    }}>
      <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
        <h2 style={{ margin: 0, fontSize: '14pt' }}>АКТ ЗВІРКИ ВЗАЄМОРОЗРАХУНКІВ</h2>
        <div style={{ marginTop: '0.5rem' }}>
          станом на {formatDate(dateTo)}
        </div>
        <div style={{ fontSize: '10pt', color: '#555' }}>
          за період з {formatDate(dateFrom)} по {formatDate(dateTo)}
        </div>
      </div>

      <p style={{ marginBottom: '0.5rem' }}>
        Цей акт складено в тому, що стан взаєморозрахунків між:
      </p>
      <table style={{ width: '100%', marginBottom: '1rem', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top', padding: '0.3rem 0.5rem' }}>
              <strong>{org?.full_name ?? org?.name ?? '—'}</strong>
              {org?.edrpou && <div style={{ fontSize: '10pt' }}>ЄДРПОУ: {org.edrpou}</div>}
            </td>
            <td style={{ width: '50%', verticalAlign: 'top', padding: '0.3rem 0.5rem' }}>
              <strong>{ctr?.full_name ?? ctr?.name ?? '—'}</strong>
              {ctr?.edrpou && <div style={{ fontSize: '10pt' }}>ЄДРПОУ: {ctr.edrpou}</div>}
              {ctr?.inn && <div style={{ fontSize: '10pt' }}>ІПН: {ctr.inn}</div>}
            </td>
          </tr>
        </tbody>
      </table>

      {contract && (
        <p style={{ marginBottom: '0.5rem' }}>
          За договором: <strong>{contract.code} — {contract.name}</strong>
        </p>
      )}

      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '10pt', marginTop: '1rem' }}>
        <thead>
          <tr style={{ borderTop: '1px solid #000', borderBottom: '1px solid #000' }}>
            <th style={{ padding: '0.3rem', textAlign: 'left', width: '15%' }}>Дата</th>
            <th style={{ padding: '0.3rem', textAlign: 'left' }}>Документ</th>
            <th style={{ padding: '0.3rem', textAlign: 'right', width: '15%' }}>Дебет</th>
            <th style={{ padding: '0.3rem', textAlign: 'right', width: '15%' }}>Кредит</th>
            <th style={{ padding: '0.3rem', textAlign: 'right', width: '15%' }}>Сальдо</th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ borderBottom: '1px solid #ccc' }}>
            <td style={{ padding: '0.3rem' }} colSpan={4}>
              <em>Сальдо на початок періоду</em>
            </td>
            <td style={{ padding: '0.3rem', textAlign: 'right', fontWeight: 600 }}>
              {fmt(act.data.opening_balance)}
            </td>
          </tr>
          {act.data.rows.map((r) => (
            <tr key={r.document_id} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: '0.3rem' }}>{formatDate(r.movement_date)}</td>
              <td style={{ padding: '0.3rem' }}>
                {DOC_LABELS[r.document_type] ?? r.document_type}
                {r.document_number && <> №{r.document_number}</>}
              </td>
              <td style={{ padding: '0.3rem', textAlign: 'right' }}>
                {Number(r.debit) > 0 ? fmt(r.debit) : ''}
              </td>
              <td style={{ padding: '0.3rem', textAlign: 'right' }}>
                {Number(r.credit) > 0 ? fmt(r.credit) : ''}
              </td>
              <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(r.balance)}</td>
            </tr>
          ))}
          <tr style={{ borderTop: '1px solid #000', borderBottom: '1px solid #000', fontWeight: 600 }}>
            <td style={{ padding: '0.3rem' }} colSpan={2}>Оборот за період</td>
            <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(act.data.turnover_debit)}</td>
            <td style={{ padding: '0.3rem', textAlign: 'right' }}>{fmt(act.data.turnover_credit)}</td>
            <td></td>
          </tr>
          <tr style={{ borderBottom: '2px solid #000', fontWeight: 700 }}>
            <td style={{ padding: '0.3rem' }} colSpan={4}>
              <em>Сальдо на кінець періоду</em>
            </td>
            <td style={{ padding: '0.3rem', textAlign: 'right' }}>
              {fmt(act.data.closing_balance)}
            </td>
          </tr>
        </tbody>
      </table>

      <p style={{ marginTop: '1rem', fontSize: '11pt' }}>
        <strong>{balanceText}</strong>
      </p>

      <table style={{ width: '100%', marginTop: '3rem', borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={{ width: '50%', verticalAlign: 'top', padding: '0.3rem 0.5rem' }}>
              <div>Від {org?.name ?? '—'}:</div>
              <div style={{ marginTop: '2.5rem', borderTop: '1px solid #000', paddingTop: '0.2rem', textAlign: 'center' }}>
                __________________________
              </div>
              <div style={{ textAlign: 'center', fontSize: '9pt' }}>(підпис, П.І.Б.)</div>
              <div style={{ marginTop: '0.5rem' }}>М.П.</div>
            </td>
            <td style={{ width: '50%', verticalAlign: 'top', padding: '0.3rem 0.5rem' }}>
              <div>Від {ctr?.name ?? '—'}:</div>
              <div style={{ marginTop: '2.5rem', borderTop: '1px solid #000', paddingTop: '0.2rem', textAlign: 'center' }}>
                __________________________
              </div>
              <div style={{ textAlign: 'center', fontSize: '9pt' }}>(підпис, П.І.Б.)</div>
              <div style={{ marginTop: '0.5rem' }}>М.П.</div>
            </td>
          </tr>
        </tbody>
      </table>

      <div className="print-toolbar" style={{
        position: 'fixed', bottom: '1rem', right: '1rem',
        background: 'white', padding: '0.5rem', borderRadius: 4,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}>
        <button onClick={() => window.print()} className="primary">🖨 Друкувати</button>
      </div>
    </div>
  );
}
